import { ref } from 'vue';
import { isEmpty, parseQueryParams } from '@/libs/functions';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store';

/**
 * 创建一个可以被重置的 ref
 * @param options - 其他选项
 * @returns {object} 包含 resetRef 方法的对象
 */
export const createResetRef = <T>(options: T) => {
  const a = ref<T>(options);
  const resetRef = () => {};

  return {
    resetRef,
  };
};

// @ts-ignore
const ensureDecodeURIComponent = (url: string) => {
  if (url.startsWith('%')) {
    return ensureDecodeURIComponent(decodeURIComponent(url));
  }
  return url;
};

/**
 * 将URL中的微信或支付宝授权code清除
 * @param url_str
 */
export const removeCodeQuery = (url_str: string) => {
  // 获取当前 URL
  const url = new URL(url_str);

  // 使用 URLSearchParams 来操作查询参数
  const params = new URLSearchParams(url.search);

  // 检查并删除指定的参数 code
  if (params.has('code')) {
    params.delete('code');
  }

  // 检查并删除指定的参数 auth_code
  if (params.has('auth_code')) {
    params.delete('auth_code');
  }

  // 重新构建 URL 的查询字符串
  url.search = params.toString();

  // 返回新的 URL 字符串
  return url.toString();
};

/**
 * 解析 url 得到 path 和 query
 * 比如输入url: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor
 * 输出: {path: /pages/login/index, query: {redirect: /pages/demo/base/route-interceptor}}
 */
export const getUrlObj = (url: string) => {
  const [path, queryStr] = url.split('?');
  // console.log(path, queryStr)

  if (!queryStr) {
    return {
      path,
      query: {},
    };
  }
  const query: Record<string, string> = {};
  queryStr.split('&').forEach((item) => {
    const [key, value] = item.split('=');
    // console.log(key, value)
    query[key] = ensureDecodeURIComponent(value); // 这里需要统一 decodeURIComponent 一下，可以兼容h5和微信y
  });
  return { path, query };
};

/**
 * 格式化配置数据
 * @param data
 */
export function formatConfigValue(data: Recordable[]): Record<string, any> {
  const obj = {};

  data.forEach((item) => {
    // @ts-ignore
    obj[item.config_key] = item.value;
  });

  return obj;
}

/**
 * @description 为了适配配置对象，递归展平多层对象
 * @param {object} obj 需要展平的对象
 * @param {string} parentKey 父级key（递归用）
 * @param {string} separator key分隔符，默认 '__'
 */
export function flattenData(obj: Record<string, any>, parentKey = '', separator = '__'): Recordable {
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const value = obj[key];
    const newKey = parentKey ? parentKey + separator + key : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const nested = flattenData(value, newKey, separator);
      Object.assign(result, nested);
    } else {
      result[newKey] = value;
    }
  }
  return result;
}

/**
 * @description 递归还原被展平的对象
 * @param {object} obj 需要还原的对象
 * @param {string} separator key分隔符，默认 '__'
 */
export function unFlattenData(obj: Record<string, any>, separator = '__'): Record<string, any> {
  const result: Record<string, any> = {};
  for (const flatKey in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, flatKey)) continue;
    const value = obj[flatKey];
    const keys = flatKey.split(separator);
    let current = result;
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (i === keys.length - 1) {
        current[k] = value;
      } else {
        if (!current[k] || typeof current[k] !== 'object') {
          current[k] = {};
        }
        current = current[k];
      }
    }
  }
  return result;
}

/**
 * 判断当前是否为微信浏览器环境
 * @return {Boolean}
 */
export const isWeixinBrowser = (): boolean => {
  return navigator.userAgent.toLowerCase().indexOf('micromessenger') !== -1;
};

/**
 * 判断当前是否为支付宝浏览器环境
 * @return {Boolean}
 */
export const isAlipayBrowser = (): boolean => {
  return navigator.userAgent.toLowerCase().indexOf('alipay') !== -1;
};

/**
 * 支付宝浏览器jsapi支付
 * @param config 配置参数
 * @returns
 */
export const aliJsApiPay = (config: Recordable) => {
  return new Promise((resolve, reject) => {
    if (!isAlipayBrowser()) {
      reject('请在支付宝App中使用');
    }

    const callback = () => {
      // @ts-ignore
      window.AlipayJSBridge.call('tradePay', config, function (res: any) {
        let is_success: boolean;
        let message: string;

        if (res.resultCode == '9000') {
          is_success = true;
          message = '支付成功';
        } else if (res.resultCode == '6001') {
          is_success = false;
          message = '用户取消支付';
        } else {
          is_success = false;
          message = '支付失败';
        }

        resolve({
          is_success,
          message,
        });
      });
    };
    // @ts-ignore
    if (window.AlipayJSBridge) {
      callback && callback();
    } else {
      document.addEventListener('AlipayJSBridgeReady', callback, false);
    }
  });
};

/**
 * 微信浏览器jsapi支付
 * @param config
 */
export const wechatJsApiPay = (config) => {
  return new Promise((resolve, reject) => {
    if (!isWeixinBrowser()) {
      reject('请在微信环境中使用');
    }

    const callback = () => {
      // @ts-ignore
      window.WeixinJSBridge.invoke('getBrandWCPayRequest', config, function (res) {
        let is_success: boolean;
        let message: string;

        if (res.err_msg == 'get_brand_wcpay_request:ok') {
          is_success = true;
          message = '支付成功';
        } else if (res.err_msg == 'get_brand_wcpay_request:cancel') {
          is_success = false;
          message = '用户取消支付';
        } else {
          is_success = false;
          message = '支付失败';
        }

        resolve({
          is_success,
          message,
        });
      });
    };

    // @ts-ignore
    if (window.WeixinJSBridge) {
      callback && callback();
    } else {
      document.addEventListener('WeixinJSBridgeReady', callback, false);
    }
  });
};

/**
 * 获取汇付支付 trade_type
 */
export function getTradeType() {
  // #ifdef MP-WEIXIN
  return 'T_MINIAPP';
  // #endif

  // #ifdef MP-ALIPAY
  return 'A_JSAPI';
  // #endif

  // #ifdef H5
  if (isWeixinBrowser()) {
    return 'T_JSAPI';
  } else if (isAlipayBrowser()) {
    return 'A_JSAPI';
  } else {
    return '';
  }
  //  #endif
}

/**
 * 发起支付
 * @param orderInfo
 * @param success
 * @param error
 */
export function requestPayment(orderInfo: Recordable, success: any = null, error: any = null) {
  uni.showLoading({
    mask: true,
    title: '支付中...',
  });
  // #ifdef H5
  const paymentMethod = isAlipayBrowser() ? aliJsApiPay : wechatJsApiPay;
  paymentMethod(orderInfo)
    .then((response: Recordable) => {
      if (response.is_success) {
        typeof success === 'function' && success();
      } else {
        uni.$u.toast(response.message);
        typeof error === 'function' && error();
      }
    })
    .catch(() => {
      typeof error === 'function' && error();
    })
    .finally(() => {
      uni.hideLoading();
    });
  // #endif

  // #ifdef MP
  uni.requestPayment({
    // #ifdef MP-ALIPAY
    provider: 'alipay',
    // #endif

    // #ifdef MP-WEIXIN
    // @ts-ignore
    provider: 'wxpay',
    // #endif
    ...orderInfo,
    success: (res) => {
      uni.hideLoading();

      // #ifdef MP-WEIXIN
      if (res?.errMsg === 'requestPayment:ok') {
        typeof success === 'function' && success();
        return true;
      }
      // #endif

      // #ifdef MP-ALIPAY
      if (res.resultCode == '9000') {
        typeof success === 'function' && success();
        return true;
      }
      // #endif

      typeof error === 'function' && error(error);
    },
    fail: (err) => {
      uni.hideLoading();

      if (err?.errMsg === 'requestPayment:fail cancel') {
        uni.$u.toast('用户取消支付');
      } else {
        uni.$u.toast('支付失败');
      }

      typeof error === 'function' && error(err);
    },
  });
  // #endif
}

/**
 * 关闭当前浏览器页面
 */
export const closeBrowser = () => {
  try {
    if (isWeixinBrowser()) {
      // @ts-ignore
      window.WeixinJSBridge.invoke('closeWindow');
    } else if (isAlipayBrowser()) {
      // @ts-ignore
      window.AlipayJSBridge.call('closeWebview');
    } else {
      window.close();
    }
  } catch (error) {
    console.error('Failed to close browser:', error);
  }
};

/**
 * 创建一个分页数据
 * @param data
 */
export function createPagination(data: any) {
  return {
    data,
    total: data?.length ?? 1,
    current_page: 1,
    last_page: 1,
  };
}

/**
 * 从二维码字符串中提取查询参数部分
 */
function extractQueryString(qrCode: string): string {
  return decodeURIComponent(qrCode).split('/').pop()!.split('?').shift() || '';
}

/**
 * 解析扫码参数
 * @param options
 */
export function parseScanParams(options: Recordable): [boolean, Recordable] {
  const { scanQuery } = storeToRefs(useUserStore());

  // 定义各种可能的扫码参数来源
  const scanSources = [
    () =>
      !isEmpty(options.q) && {
        query: extractQueryString(options.q),
      },
    () =>
      !isEmpty(options.scene) && {
        query: decodeURIComponent(options.scene),
      },
    () =>
      !isEmpty(options.scan) && {
        query: options,
        isRaw: true,
      },
    () =>
      !isEmpty(scanQuery.value.qrCode) && {
        query: extractQueryString(scanQuery.value.qrCode),
      },
  ];

  // 遍历所有可能的扫码参数来源
  for (const getSource of scanSources) {
    const source: Recordable = getSource();
    if (source) {
      const query = source.isRaw ? source.query : parseQueryParams(source.query as string);

      return [true, query];
    }
  }

  return [false, options];
}
